import { playCash } from "../common/cashSound";
// ─── Audio cues ─────────────────────────────────────────────────────────────
// A worker who reads little relies on SOUND + COLOR. Tones are synthesized
// with the Web Audio API (no asset files) and an Arabic phrase is spoken
// when the browser has an Arabic voice (best-effort, silently skipped if not).

const STORAGE_KEY = "scanner.sound";

export const isSoundEnabled = (): boolean => {
    try {
        return localStorage.getItem(STORAGE_KEY) !== "off";
    } catch {
        return true;
    }
};

export const setSoundEnabled = (on: boolean) => {
    try {
        localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
    } catch {
        /* ignore */
    }
};

let ctx: AudioContext | null = null;
const getCtx = (): AudioContext | null => {
    try {
        const Ctor = window.AudioContext || (window as any).webkitAudioContext;
        if (!Ctor) return null;
        if (!ctx) ctx = new Ctor();
        if (ctx.state === "suspended") ctx.resume();
        return ctx;
    } catch {
        return null;
    }
};

// Scanner keystrokes count as user activation, so the first scan unlocks audio.
export const unlockAudio = () => {
    getCtx();
};

const tone = (
    freq: number,
    startSec: number,
    durSec: number,
    type: OscillatorType = "sine",
    gain = 0.35
) => {
    const c = getCtx();
    if (!c) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    const t0 = c.currentTime + startSec;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec);
    osc.connect(g);
    g.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + durSec + 0.05);
};

const speak = (text: string) => {
    try {
        const synth = window.speechSynthesis;
        if (!synth) return;
        const voices = synth.getVoices();
        const voice = voices.find((v) => v.lang.toLowerCase().startsWith("ar"));
        if (!voice) return; // no Arabic voice installed → tones only
        synth.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.voice = voice;
        u.lang = voice.lang;
        u.rate = 0.95;
        synth.speak(u);
    } catch {
        /* ignore */
    }
};

// Warm the voice list (Chrome loads it asynchronously).
try {
    window.speechSynthesis?.getVoices();
    window.speechSynthesis?.addEventListener?.("voiceschanged", () => window.speechSynthesis.getVoices());
} catch {
    /* ignore */
}

export const playGo = (say?: string) => {
    if (!isSoundEnabled()) return;
    tone(660, 0, 0.12);
    tone(990, 0.14, 0.22);
    if (say) setTimeout(() => speak(say), 350);
};

export const playStop = (say?: string) => {
    if (!isSoundEnabled()) return;
    tone(170, 0, 0.35, "sawtooth", 0.3);
    tone(170, 0.45, 0.35, "sawtooth", 0.3);
    tone(170, 0.9, 0.5, "sawtooth", 0.3);
    if (say) setTimeout(() => speak(say), 1400);
};

export const playCaution = (say?: string) => {
    if (!isSoundEnabled()) return;
    tone(520, 0, 0.1, "square", 0.2);
    tone(520, 0.16, 0.1, "square", 0.2);
    tone(520, 0.32, 0.1, "square", 0.2);
    if (say) setTimeout(() => speak(say), 500);
};

// New order arrived: the cash register "ka-ching" (user 2026-10-25: a money sound instead of the spoken "وصل طلب جديد")
// — the message stays on screen, it is no longer read aloud.
export const playNew = (_say?: string) => {
    if (!isSoundEnabled()) return;
    playCash(getCtx());
};

export const playNotFound = (say?: string) => {
    if (!isSoundEnabled()) return;
    tone(420, 0, 0.15, "triangle");
    tone(260, 0.18, 0.35, "triangle");
    if (say) setTimeout(() => speak(say), 600);
};
